// Try it and the demo session in the web app (TOM-868, cloud ADR 0419).
//
// A visitor with no account opens Try it from the home page. `POST
// /v1/demo/try` gives them a guest account that co-owns one demo boat, and a
// session for it (api_contracts.md §2.8). `GET /v1/me` then carries
// `demo: { expiresAt }`. This module holds the decisions the pages make from
// that: whether to show the Try it link, what the banner says, what a failed
// Try it says, when a guest's input extends the lease, and what a guest's
// refused or ended request does.
//
// The server enforces every limit. The web only hides what a guest cannot use
// and explains a refusal.

/** The lease a guest's `GET /v1/me` reports. */
export interface DemoLease {
  expiresAt: string;
}

/** Whether a `/v1/me` record is a Try it guest's. An older server sends no `demo`. */
export function isDemoGuest(user: { demo?: DemoLease | null } | null | undefined): boolean {
  return user?.demo != null;
}

/**
 * Whether the home page shows Try it: the server has it on, and the visitor is
 * known not to be signed in to an account of their own. A guest still sees it; Try it
 * opens their own demo boat again.
 */
export function showTryItLink(
  config: { tryIt?: boolean } | null | undefined,
  status: 'unknown' | 'authenticated' | 'anonymous',
  user: { demo?: DemoLease | null } | null | undefined
): boolean {
  if (config?.tryIt !== true) return false;
  // Not until the session probe answers, so a signed-in owner never sees the
  // link appear and go.
  if (status === 'unknown') return false;
  return !(status === 'authenticated' && !isDemoGuest(user));
}

/**
 * When the demo copy ends, in the viewer's local time: "4:30 PM", or with the
 * weekday when it is not today ("Sat 4:30 PM"). A lease lasts at most 24
 * hours, so the weekday is enough.
 */
export function demoEndsAt(
  expiresAt: string,
  nowMs: number,
  opts: { locale?: string; timeZone?: string } = {}
): string {
  const end = new Date(expiresAt);
  const zone = opts.timeZone === undefined ? {} : { timeZone: opts.timeZone };
  const dayKey = (d: Date) =>
    d.toLocaleDateString('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', ...zone });
  const time = end.toLocaleTimeString(opts.locale, {
    hour: 'numeric',
    minute: '2-digit',
    ...zone,
  });
  if (dayKey(end) === dayKey(new Date(nowMs))) return time;
  const weekday = end.toLocaleDateString(opts.locale, { weekday: 'short', ...zone });
  return `${weekday} ${time}`;
}

/** The banner's first sentence. */
export function demoBannerLead(endsAt: string): string {
  return `This is your own copy of a demo boat until ${endsAt}.`;
}

/** The banner's other two sentences. */
export const DEMO_BANNER_DETAIL =
  'Changes are discarded after that. The data replays five days built from Gallivant RP’s record, with simulated systems.';

/** The banner's whole text, for a lease ending at `expiresAt`. */
export function demoBannerText(
  expiresAt: string,
  nowMs: number,
  opts: { locale?: string; timeZone?: string } = {}
): string {
  return `${demoBannerLead(demoEndsAt(expiresAt, nowMs, opts))} ${DEMO_BANNER_DETAIL}`;
}

/** What the page says when a guest's request is refused with `403 demo_read_only`. */
export const DEMO_READ_ONLY_NOTE = 'That is not available in the demo.';

/** What `/try` says after a guest's lease ended. */
export const DEMO_ENDED_NOTE =
  'Your demo copy ended, and the changes made in it were discarded. You can start a new one.';

/** How a failed `POST /v1/demo/try` is shown. */
export interface TryItFailure {
  kind: 'busy' | 'off' | 'captcha' | 'rate' | 'signed-in' | 'other';
  message: string;
  /** Whether the page offers Try again. */
  retry: boolean;
  /** A link the page shows with the message, in place of Back to the home page. */
  link?: { to: string; label: string };
}

/** The failure for an error thrown by `api('/v1/demo/try')`. */
export function tryItFailure(err: unknown): TryItFailure {
  const e = err as { status?: number; body?: unknown };
  const code =
    typeof e.body === 'object' && e.body !== null && 'error' in e.body
      ? (e.body as { error?: unknown }).error
      : undefined;
  if (e.status === 503 && code === 'demo_busy') {
    return {
      kind: 'busy',
      message: 'All demo boats are in use right now. Try again in a few minutes.',
      retry: true,
    };
  }
  if (e.status === 409 && code === 'signed_in') {
    // The browser holds an account's session. Try it would replace it.
    return {
      kind: 'signed-in',
      message: 'You are signed in to your own account, so Try it does not start a demo.',
      retry: false,
      link: { to: '/dashboard', label: 'Go to your boats' },
    };
  }
  if (e.status === 404) {
    return { kind: 'off', message: 'Try it is not available right now.', retry: false };
  }
  if (e.status === 400 && code === 'captcha_failed') {
    return {
      kind: 'captcha',
      message: 'We could not confirm this browser. Try again.',
      retry: true,
    };
  }
  if (e.status === 429) {
    return {
      kind: 'rate',
      message: 'Too many requests from this network. Try again in a minute.',
      retry: true,
    };
  }
  return {
    kind: 'other',
    message: 'We could not get a demo boat ready. Try again.',
    retry: true,
  };
}

/**
 * The message of a `429 demo_limit` refusal (TOM-868), or null for any other
 * failed answer. A guest past a cap gets it from a Neo turn, a page
 * draft or an alarm regeneration, and the page shows it as it is.
 */
export function demoLimitMessage(status: number, body: unknown): string | null {
  if (status !== 429 || typeof body !== 'object' || body === null) return null;
  const { error, message } = body as { error?: unknown; message?: unknown };
  return error === 'demo_limit' && typeof message === 'string' && message.trim() !== ''
    ? message
    : null;
}

// ----- The guest flag, kept for this tab -----
//
// Set whenever `/v1/me` answers, so that a 401 after the lease ended, even
// after a reload, sends the visitor to `/try` rather than to sign-in. Kept in
// sessionStorage, with a copy in memory for when storage is unavailable.

const GUEST_KEY = 'nm.demoGuest';
let guestInMemory = false;

/** Record whether the signed-in user is a Try it guest. */
export function markDemoGuest(on: boolean): void {
  guestInMemory = on;
  if (!on) forgetDemoTouches();
  try {
    if (on) window.sessionStorage.setItem(GUEST_KEY, '1');
    else window.sessionStorage.removeItem(GUEST_KEY);
  } catch {
    // No storage (a private window, or no window under test): memory only.
  }
}

/** Whether this tab's user was a Try it guest when `/v1/me` last answered. */
export function wasDemoGuest(): boolean {
  if (guestInMemory) return true;
  try {
    return window.sessionStorage.getItem(GUEST_KEY) === '1';
  } catch {
    return false;
  }
}

// ----- Extending the lease on user input -----
//
// Only the guest's own input extends the lease (TOM-868). The banner listens
// for pointerdown and keydown and sends `POST /v1/demo/touch`, at most once a
// minute. The server sets the lease to end two hours after the touch, and at
// most 24 hours after the lease began, and answers `{ expiresAt }`. No timer
// calls the server for a guest, so a tab left open lets the lease end.

/** The shortest time between two touches. */
export const DEMO_TOUCH_MIN_MS = 60_000;

/**
 * Whether user input at `nowMs` sends a touch. `lastSentMs` is when the last
 * touch was sent, or null before the first. A touch counts from when it is
 * sent, so a slow or failed touch does not let input send more.
 */
export function shouldSendDemoTouch(lastSentMs: number | null, nowMs: number): boolean {
  return lastSentMs === null || nowMs - lastSentMs >= DEMO_TOUCH_MIN_MS;
}

/** The lease end in a `POST /v1/demo/touch` answer, or null for any other body. */
export function demoTouchExpiresAt(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const { expiresAt } = body as { expiresAt?: unknown };
  return typeof expiresAt === 'string' && !Number.isNaN(Date.parse(expiresAt)) ? expiresAt : null;
}

/**
 * The lease end the banner shows. `touch` is the latest touch answer and the
 * `/v1/me` value it followed. It is used while `/v1/me` still reports that
 * value. A newer `/v1/me` answer replaces it: the server read the lease again.
 */
export function demoLeaseEnd(
  meExpiresAt: string,
  touch: { afterMe: string; expiresAt: string } | null
): string {
  return touch !== null && touch.afterMe === meExpiresAt ? touch.expiresAt : meExpiresAt;
}

// The throttle and the latest answer are kept for the page, not for one
// banner: the banner mounts again when the visitor moves between the boat's
// pages and the account pages.
let lastTouchSentMs: number | null = null;
let latestTouch: { afterMe: string; expiresAt: string } | null = null;

/** When the last touch was sent in this page, or null. */
export function lastDemoTouchSentMs(): number | null {
  return lastTouchSentMs;
}

/** Record that a touch was sent at `nowMs`. */
export function noteDemoTouchSent(nowMs: number): void {
  lastTouchSentMs = nowMs;
}

/** The latest touch answer, and the `/v1/me` value it followed. */
export function latestDemoTouch(): { afterMe: string; expiresAt: string } | null {
  return latestTouch;
}

/** Record a touch answer. */
export function noteDemoTouchAnswer(afterMe: string, expiresAt: string): void {
  latestTouch = { afterMe, expiresAt };
}

/** Forget the throttle and the latest answer: the guest left, or the lease ended. */
export function forgetDemoTouches(): void {
  lastTouchSentMs = null;
  latestTouch = null;
}

// ----- Signals from api() -----

/** Sent on `window` when a guest's change is refused with `403 demo_read_only`. */
export const DEMO_READ_ONLY_EVENT = 'nm:demo-read-only';
/** Sent on `window` when a guest's request gets 401 after renewal: the lease ended. */
export const DEMO_ENDED_EVENT = 'nm:demo-ended';

/**
 * What a failed request means for a guest, or null. A refused read gives no
 * note: the shell reads some routes a guest is denied in the background, and
 * a note nobody asked for would only confuse. A 401 from a sign-in route is
 * about the credentials just sent, not about the lease.
 */
export function demoSignalFor(
  path: string,
  method: string,
  status: number,
  body: unknown,
  wasGuest: boolean
): 'read-only' | 'ended' | null {
  if (status === 403 && method !== 'GET') {
    const code =
      typeof body === 'object' && body !== null ? (body as { error?: unknown }).error : undefined;
    return code === 'demo_read_only' ? 'read-only' : null;
  }
  if (status === 401 && wasGuest && !path.startsWith('/v1/auth/')) return 'ended';
  return null;
}

// ----- Reset demo (TOM-892, cloud ADR 0436) -----
//
// `POST /v1/demo/reset` gives the visitor a fresh guest on the same boat, with
// new cookies, and erases the old guest. Requests still in flight with the old
// session then get 401, which is not the lease ending: while a reset runs, and
// until the page reloads after it, no 401 sends the visitor to /try.

let resetting = false;

/** Record that a Reset demo is running (true) or failed (false). */
export function markDemoResetting(on: boolean): void {
  resetting = on;
}

/** The confirm Reset demo asks. */
export const DEMO_RESET_CONFIRM =
  'Reset the demo? The boat goes back to the start of its third day, and your changes and chats are discarded.';

/** What the banner says when a Reset demo fails. */
export const DEMO_RESET_FAILED_NOTE = 'The demo could not be reset. Try again in a few minutes.';

/** Send the signal for a failed request, if it has one. */
export function signalDemoFailure(
  path: string,
  method: string,
  status: number,
  body: unknown
): void {
  if (status !== 401 && status !== 403) return;
  if (status === 401 && resetting) return;
  const signal = demoSignalFor(path, method, status, body, wasDemoGuest());
  if (signal === null || typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(signal === 'read-only' ? DEMO_READ_ONLY_EVENT : DEMO_ENDED_EVENT)
  );
}

/**
 * The browser's IANA time zone, sent with Try it and Reset demo so the demo
 * boat plays on the visitor's own clock (TOM-917). Undefined when the browser
 * names none; the server then uses the demo's own zone.
 */
export function browserTimeZone(): string | undefined {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === 'string' && zone.length > 0 ? zone : undefined;
  } catch {
    return undefined;
  }
}
